import type Phaser from "phaser";
import { COMPILED_COLLISION_CELLS_PER_TILE } from "daedalus-core";
import { FOOT_HALF_WIDTH, FOOT_HEIGHT, SIXTEENTHS, SOLID, type CollisionGrid } from "odyssey-shared";
import { gameReport } from "./report.ts";

const DEBUG_DEPTH = 1_000_000;
const DEBUG_ALPHA = 0.6;

/**
 * The F8 collision debug view: the blocked cells the client predicts against (red solid, blue water, orange hole)
 * over the visible view and the local feet box in yellow, so collision and art can be compared on screen. Its
 * overlay is made the first time it draws and sits above everything in the world layer.
 */
export class CollisionDebug {
  private overlay: Phaser.GameObjects.Graphics | null = null;
  private on = false;

  /**
   * @param scene The scene the overlay draws in.
   */
  public constructor(private readonly scene: Phaser.Scene) {}

  /**
   * Turns the view on or off and says which in the report's `debug`.
   */
  public toggle(): void {
    this.on = !this.on;
    gameReport.debug = this.on;
  }

  /**
   * While the view is on, draws the grid's blocked cells inside the main camera's view and the local feet box;
   * off, or with no grid, the overlay is cleared.
   *
   * @param grid The collision grid the client predicts against.
   * @param feetX Local feet x in world pixels.
   * @param feetY Local feet y in world pixels.
   * @param scale World pixels per sixteenth.
   */
  public draw(grid: CollisionGrid | null, feetX: number, feetY: number, scale: number): void {
    if (!this.on || grid === null) {
      this.overlay?.clear();
      return;
    }
    if (this.overlay === null) {
      this.overlay = this.scene.add.graphics().setDepth(DEBUG_DEPTH);
    }
    const g = this.overlay;
    const cellPixels = (SIXTEENTHS * scale) / COMPILED_COLLISION_CELLS_PER_TILE;
    const view = this.scene.cameras.main.worldView;
    g.clear();
    const x0 = Math.max(0, Math.floor(view.x / cellPixels));
    const y0 = Math.max(0, Math.floor(view.y / cellPixels));
    const x1 = Math.min(grid.width - 1, Math.ceil((view.x + view.width) / cellPixels));
    const y1 = Math.min(grid.height - 1, Math.ceil((view.y + view.height) / cellPixels));
    for (let cy = y0; cy <= y1; cy += 1) {
      for (let cx = x0; cx <= x1; cx += 1) {
        const byte = grid.cells[cy * grid.width + cx] as number;
        if (byte === 0) {
          continue;
        }
        g.fillStyle((byte & SOLID) !== 0 ? 0xff3b30 : (byte & 2) !== 0 ? 0x2f80ff : 0xffa726, DEBUG_ALPHA);
        g.fillRect(cx * cellPixels, cy * cellPixels, cellPixels, cellPixels);
      }
    }
    g.lineStyle(1, 0xffe14d, 1);
    g.strokeRect(feetX - FOOT_HALF_WIDTH * scale, feetY - FOOT_HEIGHT * scale, FOOT_HALF_WIDTH * 2 * scale, FOOT_HEIGHT * scale);
  }
}
